import { type FormEvent, useState } from 'react';
import { Info, Loader2, Sparkles, User, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { useDiagnosticStore } from '../../../stores/diagnostic';
import { apiFetch, ApiError } from '../../../lib/api';
import {
  trackSubmitStart,
  trackSubmitSuccess,
  trackSubmitFailure,
  trackValidationError,
  readAttribution,
} from '../../../lib/tracking';
import { submitDraft } from '../../../hooks/useDraftAutosave';
import { cn } from '../../../lib/cn';
import { easeStripe } from '../../../lib/motion';
import { TrustBar } from '../../conversion/TrustBar';
import { Eyebrow } from './types';

export function Step4Lead() {
  const store = useDiagnosticStore();
  // Champs stockés dans le store persisté : la saisie survit à un F5 / une reprise.
  const { firstName, lastName, email, phone, consentMarketing } = store.contact;
  const setFirstName = (v: string) => store.patchContact({ firstName: v });
  const setLastName = (v: string) => store.patchContact({ lastName: v });
  const setEmail = (v: string) => store.patchContact({ email: v });
  const setPhone = (v: string) => store.patchContact({ phone: v });
  const setConsentMarketing = (v: boolean) => store.patchContact({ consentMarketing: v });
  const [emailError, setEmailError] = useState('');

  const diag_ctx = { funnelId: 'diagnostic' as const, intent: 'cee', source: 'diagnostic' };

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setEmailError('');

    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError('Adresse e-mail invalide');
      trackValidationError(diag_ctx, 'step4', ['email']);
      return;
    }

    trackSubmitStart(diag_ctx, { segment: store.segment });
    store.setLoading(true);
    try {
      const result = await apiFetch<{ id: string }>('/api/leads', {
        json: {
          email: email.trim(),
          phone: phone || undefined,
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          source: 'diagnostic',
          segment: store.segment,
          intent: 'cee',
          consentMarketing,
          message: store.estimation ? JSON.stringify(store.estimation) : undefined,
          ...readAttribution(),
        },
      });
      store.setLead(result.id);
      trackSubmitSuccess(diag_ctx, result.id, { email: email.trim(), segment: store.segment });
      void submitDraft('diagnostic', result.id);
    } catch (err) {
      trackSubmitFailure(diag_ctx, err instanceof ApiError ? 'api' : 'unknown');
      store.setError(err instanceof ApiError ? err.message : "Erreur lors de l'envoi.");
    } finally {
      store.setLoading(false);
    }
  }

  const est = store.estimation;

  return (
    <div>
      <Eyebrow>Étape 4 sur 5</Eyebrow>

      {est && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: easeStripe }}
          className="mt-3 overflow-hidden rounded-2xl border border-brand-200 bg-gradient-to-br from-brand-50 via-white to-accent-50/30"
        >
          <div className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-600">
                  Votre estimation CEE
                </p>
                <p className="mt-1.5 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
                  {est.primeMin.toLocaleString('fr-FR')} €
                  <span className="mx-1 text-xl text-stone-400 sm:text-2xl">–</span>
                  {est.primeMax.toLocaleString('fr-FR')} €
                </p>
              </div>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-100">
                <Zap className="h-5 w-5 text-brand-600" strokeWidth={1.7} />
              </span>
            </div>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-stone-100/80">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: '62%' }}
                transition={{ duration: 0.8, delay: 0.15, ease: easeStripe }}
                className="h-full rounded-full bg-gradient-to-r from-brand-400 to-accent-500"
              />
            </div>
            <div className="mt-1.5 flex justify-between text-[10px] text-stone-400">
              <span>Min</span>
              <span>Max</span>
            </div>
          </div>

          {est.fichesEligibles.length > 0 && (
            <div className="border-t border-brand-100 bg-white/50 px-5 py-4">
              <p className="mb-2 text-[11px] font-semibold text-stone-500">
                {est.fichesEligibles.length} fiche{est.fichesEligibles.length > 1 ? 's' : ''} éligible{est.fichesEligibles.length > 1 ? 's' : ''} identifiée{est.fichesEligibles.length > 1 ? 's' : ''}
              </p>
              <div className="space-y-1.5">
                {est.fichesEligibles.slice(0, 3).map((f) => (
                  <div key={f.code} className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 shadow-[0_1px_3px_rgba(0,0,0,.04)]">
                    <div className="flex min-w-0 items-center gap-2">
                      <a
                        href={`/cee/fiches/${f.code}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 font-mono text-[10px] font-bold text-brand-600 underline-offset-2 hover:underline"
                      >
                        {f.code}
                      </a>
                      <span className="truncate text-[12px] text-stone-700">{f.label}</span>
                      {f.estimationType === 'computed' && (
                        <span className="shrink-0 rounded-full bg-brand-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-brand-700">
                          Calcul réel
                        </span>
                      )}
                    </div>
                    <span className="shrink-0 text-[11px] font-bold tabular-nums text-brand-700">
                      {f.estimationType === 'study' || (f.primeMin === 0 && f.primeMax === 0)
                        ? 'Sur étude'
                        : `${f.primeMin.toLocaleString('fr-FR')}–${f.primeMax.toLocaleString('fr-FR')} €`}
                    </span>
                  </div>
                ))}
                {est.fichesEligibles.length > 3 && (
                  <p className="pl-1 text-[11px] text-stone-400">
                    + {est.fichesEligibles.length - 3} autre{est.fichesEligibles.length - 3 > 1 ? 's' : ''} fiche{est.fichesEligibles.length - 3 > 1 ? 's' : ''}…
                  </p>
                )}
              </div>
            </div>
          )}
        </motion.div>
      )}

      <div className="mt-6">
        <h3 className="font-display text-lg font-bold tracking-tight text-ink">
          Recevez vos fiches CEE éligibles et le montant exact de votre prime
        </h3>
        <p className="mt-1 text-[13px] text-stone-500">
          Vos fiches CEE éligibles, le dossier à déposer et le calendrier de versement. Réponse d'un ingénieur sous 24&nbsp;h ouvrées — sans engagement.
        </p>

        <form onSubmit={onSubmit} className="mt-4 space-y-3.5">
          {/* Prénom + Nom (optionnels — améliorent la qualification du lead) */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="diag-firstname" className="text-[13px] font-semibold">
                Prénom <span className="font-normal text-stone-400">(optionnel)</span>
              </Label>
              <div className="relative mt-1.5">
                <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" aria-hidden />
                <Input
                  id="diag-firstname"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Jean"
                  className="h-11 rounded-xl border-2 border-stone-200 pl-9 focus:border-brand-400"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="diag-lastname" className="text-[13px] font-semibold">
                Nom <span className="font-normal text-stone-400">(optionnel)</span>
              </Label>
              <Input
                id="diag-lastname"
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Dupont"
                className="mt-1.5 h-11 rounded-xl border-2 border-stone-200 focus:border-brand-400"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="diag-email" className="text-[13px] font-semibold">E-mail professionnel</Label>
            <Input
              id="diag-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              required
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
              placeholder="contact@entreprise.fr"
              aria-invalid={!!emailError}
              className="mt-1.5 h-11 rounded-xl border-2 border-stone-200 focus:border-brand-400"
            />
            {emailError && (
              <p className="mt-1 text-[12px] font-medium text-red-600" role="alert">{emailError}</p>
            )}
          </div>

          <div>
            <Label htmlFor="diag-phone" className="text-[13px] font-semibold">
              Téléphone <span className="font-normal text-stone-400">(optionnel)</span>
            </Label>
            <Input
              id="diag-phone"
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="06 00 00 00 00"
              className="mt-1.5 h-11 rounded-xl border-2 border-stone-200 focus:border-brand-400"
            />
          </div>

          {/* Notice RGPD — base légale : exécution du service (art. 6.1.b RGPD). */}
          <div className="flex items-start gap-2 rounded-lg border border-stone-200 bg-stone-50/60 px-3 py-2.5">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400" strokeWidth={1.8} aria-hidden />
            <p className="text-[11.5px] leading-relaxed text-stone-500">
              Vos coordonnées sont traitées par Solution Éco France pour vous envoyer votre estimation et assurer le suivi de votre dossier CEE (RGPD art. 6.1.b).{' '}
              <a href="/legal/politique-confidentialite" className="font-medium text-brand-700 hover:underline">
                Politique de confidentialité
              </a>
            </p>
          </div>

          {/* Consentement marketing — optionnel (RGPD art. 6.1.a). */}
          <label className="flex cursor-pointer items-start gap-3">
            <span
              className={cn(
                'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors',
                consentMarketing ? 'border-brand-500 bg-brand-500' : 'border-stone-300 bg-white',
              )}
            >
              {consentMarketing && (
                <svg className="h-3 w-3 text-white" viewBox="0 0 12 10" fill="none" aria-hidden>
                  <path d="M1 5l3.5 3.5L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
              <input
                type="checkbox"
                checked={consentMarketing}
                onChange={(e) => setConsentMarketing(e.target.checked)}
                className="sr-only"
                aria-label="Recevoir les actualités CEE et aides énergie de Solution Éco France"
              />
            </span>
            <span className="text-[12px] leading-relaxed text-stone-600">
              Je souhaite recevoir les actualités aides &amp; primes de Solution Éco France.{' '}
              <span className="text-stone-400">(Optionnel — désabonnement en 1 clic)</span>
            </span>
          </label>

          <Button type="submit" variant="brand" size="xl" disabled={store.loading} className="w-full gap-2">
            {store.loading ? (
              <><Loader2 className="h-4 w-4 animate-spin" />Envoi en cours…</>
            ) : (
              <><Sparkles className="h-4 w-4" />Recevoir mes fiches CEE éligibles</>
            )}
          </Button>

          <TrustBar variant="inline" max={4} className="pt-1" />
        </form>
      </div>
    </div>
  );
}
